/**
 * Who the "Email service desk" button addresses.
 *
 * The recipients live on the partner company, set by an NSC Admin in the SmartTeams back-office, so
 * nothing is hard-coded in the apps. A company with nothing set has no recipients, and the button then
 * opens the mail with the address left blank. This module is the one place that parses and validates them; both apps import it.
 */

/** Upper bound on Cc recipients — a `mailto:` URL has practical length limits in mail clients. */
export const MAX_SERVICE_DESK_CC = 10;

// Deliberately simple: one `@`, a dot in the domain, and none of the characters that would let a value
// break out of a header or a list (whitespace, quotes, angle brackets, commas, semicolons, controls).
const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]+$/;

export function isValidEmailAddress(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

export interface ParsedEmailList {
  /** Valid addresses: trimmed, lowercased and de-duplicated, in the order given. */
  emails: string[];
  /** Entries that were not valid addresses, as typed. */
  invalid: string[];
}

/**
 * Split free text (or an array) on commas, semicolons, spaces and newlines — Outlook-style
 * `a@x.com; b@x.com` is accepted — and sort the entries into valid and invalid.
 */
export function parseEmailList(input: string | readonly string[] | null | undefined): ParsedEmailList {
  const parts = (Array.isArray(input) ? input : [input ?? ''])
    .flatMap(entry => String(entry).split(/[\s,;]+/))
    .map(entry => entry.trim())
    .filter(Boolean);

  const emails: string[] = [];
  const invalid: string[] = [];
  for (const part of parts) {
    if (!isValidEmailAddress(part)) {
      invalid.push(part);
      continue;
    }
    const normalized = part.toLowerCase();
    if (!emails.includes(normalized)) emails.push(normalized);
  }
  return { emails, invalid };
}

export interface ServiceDeskRecipients {
  to: string;
  cc: string[];
}

interface RecipientSource {
  to?: string | null;
  cc?: string | readonly string[] | null;
}

/**
 * Turn a company's setting into recipients, or null when it has no valid `To` (a Cc with nobody to send to is
 * not a configuration). Invalid entries are dropped rather than failing, so a bad value stored by an
 * older version can never produce a broken link; the Cc list never repeats the `To` or exceeds the cap.
 * Null means nothing is configured: the caller leaves the recipients blank rather than guess an address.
 */
export function toRecipients(source: RecipientSource | null | undefined): ServiceDeskRecipients | null {
  const to = parseEmailList(source?.to).emails[0];
  if (!to) return null;
  const cc = parseEmailList(source?.cc ?? [])
    .emails.filter(address => address !== to)
    .slice(0, MAX_SERVICE_DESK_CC);
  return { to, cc };
}
